import { Children, memo } from "react";
import { Link } from "react-router";
import { NavItem, DropdownMenu, type NavItemProps, Pencil, Button, NavTree } from "@repo/ui";
import { cn } from "@repo/utils";
import { useSidebarContext } from "./sidebar-context";
import { useSidebarActions } from "./sidebar-utils";

type Props = {
  item: NavItemProps;
  isActive: boolean;
  resolvedIcon: React.ReactNode;
  showChevron: boolean;
  defaultOpen: boolean;
  showOptionDD: boolean;
  children?: React.ReactNode;
  onClick?: () => void;
  entityId?: string;
  entityType?: "brand" | "folder" | "task";
  showTreeLines?: boolean;
};

export const SidebarRow = memo(
  ({
    item,
    isActive,
    resolvedIcon,
    showChevron,
    defaultOpen,
    showOptionDD,
    children,
    onClick,
    entityId,
    entityType,
    showTreeLines = true,
  }: Props) => {
    const dropdownMenuItems = useSidebarActions(item.label, entityId, entityType);
    const { toggleExpand, handlers } = useSidebarContext();

    const handleNavItemClick = (e?: React.MouseEvent) => {
      if (entityId) {
        if (entityType === "brand" || entityType === "folder") {
          toggleExpand(entityId);
        }

        if (entityType === "task") {
          e?.preventDefault();
          handlers.onTaskClick?.(entityId);
        }
      }

      if (onClick) {
        onClick();
      }
    };

    const size = item.size ?? "md";
    const hasChildren = Children.count(children) > 0;

    const isTask = entityType === "task";

    return (
      <div className="group/item relative">
        <div className="group/dropdown relative focus-within:z-40 hover:z-30">
          {showOptionDD && (
            <div
              className={cn(
                "pointer-events-none absolute inset-x-0 top-0 z-0 h-10 rounded-lg bg-gray-100 transition-opacity",
                "opacity-0 group-hover/dropdown:opacity-100"
              )}
            />
          )}

          <div className={cn("relative z-10", { "rounded-lg": showOptionDD })}>
            <NavItem
              as={item.to ? Link : undefined}
              to={item.to}
              icon={resolvedIcon}
              label={item.label}
              onClick={handleNavItemClick}
              isActive={isActive}
              defaultOpen={defaultOpen}
              showChevron={showChevron}
              isParent={hasChildren}
              showTreeLines={showTreeLines}
            />
          </div>

          {showOptionDD && (
            <div className="group/menu absolute top-2.5 right-7 z-20 cursor-pointer transition-opacity">
              {isTask ? (
                <Button
                  data-allow-navigation
                  align="center"
                  justify="center"
                  size={"icon_sm"}
                  onClick={(e) => {
                    e.stopPropagation();
                    const action = dropdownMenuItems.find((a) => a.id === "rename-task");
                    action?.onClick();
                  }}
                  className={cn(
                    "-mt-0.5 opacity-0 group-hover/dropdown:opacity-100 focus-visible:opacity-100"
                  )}
                >
                  <Pencil className="size-4" />
                </Button>
              ) : (
                <DropdownMenu
                  items={dropdownMenuItems}
                  icon="dots"
                  align="right"
                  placement="bottom"
                  menuClassName="mt-1 z-50"
                  buttonClassName="rounded-md focus-visible:opacity-100"
                  iconClassName={(isOpen) =>
                    cn("h-5 w-5 !p-0 transition-colors", {
                      "!bg-gxe-primary hover:!opacity-80 text-white opacity-100": isOpen,
                      "hover:bg-gray-200 text-gray-600 opacity-0 group-hover/dropdown:opacity-100":
                        !isOpen,
                    })
                  }
                />
              )}
            </div>
          )}
        </div>

        {defaultOpen && hasChildren && (
          <NavTree size={size} isOpen={defaultOpen}>
            {children}
          </NavTree>
        )}
      </div>
    );
  }
);

SidebarRow.displayName = "SidebarRow";
